import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatearRut } from '../utils/validaciones';
import './Login.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [theme, setTheme] = useState(localStorage.getItem('app-theme') || 'dark');

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('app-theme', newTheme);
    document.body.setAttribute('data-theme', newTheme);
  };
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const apiBase = isLocal ? `http://${window.location.hostname}:3010` : `${window.location.protocol}//${window.location.hostname}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const rutLimpio = username.replace(/[.\-\s]/g, '').toUpperCase();

    try {
      const resp = await fetch(`${apiBase}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rut: rutLimpio, password })
      });
      if (resp.ok) {
        const data = await resp.json();
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.removeItem('token'); // Seguridad: El token ahora va en cookie HttpOnly

        if (data.user.primer_ingreso === 1) {
          navigate('/perfil');
        } else if (['supervisor', 'superadmin', 'admin', 'jefatura'].includes(data.user.rol.toLowerCase())) {
          navigate('/dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        const errData = await resp.json();
        setError(errData.error || 'Credenciales inválidas');
      }
    } catch (err) {
      setError('Error de conexión con el servidor municipal.');
    } finally {
      setLoading(false);
    }
  };

  const [showRecover, setShowRecover] = useState(false);
  const [recoverRut, setRecoverRut] = useState('');
  const [recoverStatus, setRecoverStatus] = useState({ loading: false, success: false, error: '' });

  const handleRecoverPassword = async (e) => {
    e.preventDefault();
    setRecoverStatus({ loading: true, success: false, error: '' });
    const cleanRut = recoverRut.replace(/[.\-\s]/g, '').toUpperCase();

    try {
      const resp = await fetch(`${apiBase}/api/perfil/recuperar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rut: cleanRut })
      });
      if (resp.ok) {
        setRecoverStatus({ loading: false, success: true, error: '' });
      } else {
        const data = await resp.json();
        setRecoverStatus({ loading: false, success: false, error: data.error || 'Error al solicitar el enlace.' });
      }
    } catch (err) {
      setRecoverStatus({ loading: false, success: false, error: 'Error de conexión.' });
    }
  };

  return (
    <div className="login-container">
      {/* Theme Toggle */}
      <button 
        onClick={toggleTheme}
        className="theme-toggle-floating"
        title={theme === 'dark' ? "Cambiar a Modo Claro" : "Cambiar a Modo Oscuro"}
        style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          background: 'var(--card-bg)',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--glass-border)',
          color: theme === 'dark' ? 'var(--p-amber)' : 'var(--p-indigo)',
          width: '44px',
          height: '44px',
          borderRadius: '14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          zIndex: 1000,
          boxShadow: 'var(--shadow-lg)'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1) rotate(15deg)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1) rotate(0deg)'; }}
      >
        <i className={theme === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon'}></i>
      </button>

      {/* Animated Background */}
      <div className="login-bg-gradient"></div>

      {/* Floating Particles */}
      <div className="login-particles">
        <div className="login-particle"></div>
        <div className="login-particle"></div>
        <div className="login-particle"></div>
        <div className="login-particle"></div>
        <div className="login-particle"></div>
        <div className="login-particle"></div>
        <div className="login-particle"></div>
        <div className="login-particle"></div>
      </div>

      {/* Main Card */}
      <div className="login-main-card">
        {/* Left Side - Branding */}
        <div className="login-branding">
          <div className="login-branding-content">
            <div className="login-branding-icon">
              <i className="fa-solid fa-building-shield"></i>
            </div>
            <h1>Gactivos</h1>
            <p className="login-branding-subtitle">
              Sistema integral de gestión, trazabilidad y control de activos municipales.
            </p>

            <ul className="login-branding-features">
              <li>
                <i className="fa-solid fa-qrcode"></i>
                Trazabilidad QR en tiempo real
              </li>
              <li>
                <i className="fa-solid fa-file-signature"></i>
                Actas digitales con firma
              </li>
              <li>
                <i className="fa-solid fa-shield-halved"></i>
                Control de acceso por roles
              </li>
              <li>
                <i className="fa-solid fa-chart-pie"></i>
                Analítica de inventario
              </li>
            </ul>

          </div>
        </div>

        {/* Right Side - Form */}
        <div className="login-form-side">
          <div className="login-header">
            <h2>Bienvenido</h2>
            <p>Ingresa tus credenciales institucionales para continuar.</p>
          </div>

          {error && (
            <div className="animate-fade-in" style={{
              color: 'var(--p-rose)',
              backgroundColor: 'rgba(244, 63, 94, 0.08)',
              border: '1px solid rgba(244, 63, 94, 0.15)',
              padding: '12px 16px',
              borderRadius: '12px',
              marginBottom: '20px',
              fontSize: '13px',
              textAlign: 'center',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <i className="fa-solid fa-circle-exclamation"></i>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="input-group">
              <label>RUT Autorizado</label>
              <div className="input-wrapper">
                <i className="fa-solid fa-id-card"></i>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onBlur={(e) => setUsername(formatearRut(e.target.value))}
                  placeholder="Ej: 17.654.321-8"
                  required
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="password">Contraseña</label>
              <div className="input-wrapper">
                <i className="fa-solid fa-lock"></i>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ingresa tu contraseña"
                  required
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="forgot-password-btn" onClick={() => setShowRecover(true)}>¿Problemas al ingresar?</button>
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? <span className="spinner"></span> : (
                <>
                  <i className="fa-solid fa-right-to-bracket" style={{ marginRight: '8px' }}></i>
                  Ingresar al Portal
                </>
              )}
            </button>
          </form>

          <div className="external-systems">
            <p>Ecosistema Municipal</p>
            <button
              type="button"
              className="secondary-system-btn"
              onClick={() => {
                navigate('/espacios/login');
              }}
            >
              <i className="fa-solid fa-calendar-check"></i>
              Gestor de Espacios
            </button>
          </div>

          <div className="login-footer">
            <span>Pitrufquén · Región de la Araucanía</span>
          </div>
        </div>
      </div>

      {/* Recovery Modal */}
      {showRecover && (
        <div className="modal-overlay" onClick={() => !recoverStatus.loading && setShowRecover(false)}>
          <div className="recovery-modal" onClick={(e) => e.stopPropagation()}>
            <div className="recovery-icon">
              <i className={`fa-solid ${recoverStatus.success ? 'fa-envelope-circle-check' : 'fa-key'}`}></i>
            </div>
            
            {recoverStatus.success ? (
              <div className="animate-fade-in">
                <h3>¡Correo Enviado!</h3>
                <p>Si el RUT ingresado coincide con un usuario, recibir&aacute;s un enlace de restablecimiento en tu bandeja de entrada.</p>
                <button className="login-button" style={{ width: '100%' }} onClick={() => setShowRecover(false)}>Entendido</button>
              </div>
            ) : (
              <form onSubmit={handleRecoverPassword} className="animate-fade-in">
                <h3>Recuperar Acceso</h3>
                <p>Ingresa tu RUT para enviarte un enlace de recuperación a tu correo institucional.</p>
                
                <div className="input-group" style={{ marginBottom: '10px' }}>
                  <label>Tu RUT Registrado</label>
                  <div className="input-wrapper">
                    <i className="fa-solid fa-id-card"></i>
                    <input
                      required
                      type="text"
                      value={recoverRut}
                      onChange={(e) => setRecoverRut(e.target.value)}
                      onBlur={(e) => setRecoverRut(formatearRut(e.target.value))}
                      placeholder="12.345.678-9"
                    />
                  </div>
                </div>

                {recoverStatus.error && (
                  <div style={{ color: 'var(--p-rose)', fontSize: '13px', marginBottom: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <i className="fa-solid fa-circle-exclamation"></i> {recoverStatus.error}
                  </div>
                )}

                <div className="modal-actions">
                  <button type="submit" className="login-button" disabled={recoverStatus.loading}>
                    {recoverStatus.loading ? <span className="spinner"></span> : 'Enviar Enlace Seguro'}
                  </button>
                  <button type="button" className="btn-secondary-modal" onClick={() => setShowRecover(false)} disabled={recoverStatus.loading}>
                    Cancelar
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
